import createUseContext from "constate";
import { useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import Helpers from "../../services/helpers";
import {
  TIMESPAN_SETTINGS,
  TREND_SETTINGS,
  YAXIS_SETTINGS,
  DATA_KEY_BY_CHART,
} from "../charts/PopularCardChart/constants";
import moment from "moment";
import { generateCardTitle } from "utils/cardList";

export const POPULAR_CHART_TYPES = {
  CARD: "CARD",
  SEALED_WAX: "SEALED_WAX",
};

function usePopularChart({ type }) {
  const [pointsByDay, setPointsByDay] = useState([]);
  const [cards, setCards] = useState([]);
  const [sealedWaxes, setSealedWaxes] = useState([]);

  const [entities, lineIdentifier] = {
    [POPULAR_CHART_TYPES.CARD]: [cards, "cardId"],
    [POPULAR_CHART_TYPES.SEALED_WAX]: [sealedWaxes, "sealedWaxId"],
  }[type];

  const [lineProps, setLineProps] = useState([]);
  const [timespanSetting, setTimespanSetting] = useState(TIMESPAN_SETTINGS.DAY);
  const [timespanPoints, setTimespanPoints] = useState({
    [TIMESPAN_SETTINGS.DAY]: [],
    [TIMESPAN_SETTINGS.WEEK]: [],
    [TIMESPAN_SETTINGS.MONTH]: [],
  });
  const [trendSetting, setTrendSetting] = useState(TREND_SETTINGS.LINE_AND_TREND);
  const [yAxisSetting, setYAxisSetting] = useState(YAXIS_SETTINGS.ZERO);
  const [hoveringEntityId, setHoveringEntityId] = useState(null);
  const [hiddenLines, setHiddenLines] = useState({});
  const [selectedGraph, setSelectedGraph] = useState("valuePerDay");

  useEffect(() => {
    setTimespanPoints({
      [TIMESPAN_SETTINGS.DAY]: pointsByDay,
      [TIMESPAN_SETTINGS.WEEK]:
        TIMESPAN_SETTINGS.WEEK === timespanSetting ? calculateDataByTimespan(timespanSetting) : [],
      [TIMESPAN_SETTINGS.MONTH]:
        TIMESPAN_SETTINGS.MONTH === timespanSetting ? calculateDataByTimespan(timespanSetting) : [],
    });
  }, [pointsByDay]);

  useEffect(() => {
    setLineProps(
      lineProps.map((lineProp) => ({
        ...lineProp,
        strokeWidth: hoveringEntityId && Number(hoveringEntityId) === lineProp[lineIdentifier] ? 5 : 3,
        opacity: hoveringEntityId === null ? 1 : lineProp[lineIdentifier] === Number(hoveringEntityId) ? 1 : 0.5,
      })),
    );
  }, [hoveringEntityId]);

  const setEntityActiveAlert = (targetEntityId) => {
    setLineProps(
      lineProps.map((lineProp) => {
        if (lineProp[lineIdentifier] === Number(targetEntityId)) {
          lineProp.hasAlert = true;
        }
        return lineProp;
      }),
    );
  };

  useEffect(() => {
    const newLineProps = entities.reduce((acc, entity, idx) => {
      const name = {
        [POPULAR_CHART_TYPES.CARD]: generateCardTitle(entity, { hideSport: true }),
        [POPULAR_CHART_TYPES.SEALED_WAX]: Helpers.getSealedWaxName(entity),
      }[type];

      const baseProps = {
        [lineIdentifier]: entity.id,
        name,
        image: Helpers.pickMmImage(entity, "L") || null,
        small_image: Helpers.pickMmImage(entity, "S") || null,
        connectNulls: true,
        dot: true,
        stroke: Helpers.getColorFromPalette(idx, entities.length),
        strokeWidth: 3,
        hide: !!hiddenLines[entity.id],
        hasAlert: entity.has_alert,
      };

      switch (type) {
        case POPULAR_CHART_TYPES.CARD: {
          baseProps.nameWOGrade = generateCardTitle(entity, { hideGrade: true, hideSport: true });
          baseProps.grade = (entity.grade && (entity.grade.abbreviation || entity.grade.name)) || "";
          baseProps.isRookieCard = entity.player.rookie_years
            ? entity.player.rookie_years.includes(entity.card_set.year)
            : false;
          baseProps.popCount = entity.population_count;
          baseProps.popCountLastUpdated = entity.population_last_updated;
          baseProps.serialNumber = entity.serial_number;
          break;
        }
        case POPULAR_CHART_TYPES.SEALED_WAX: {
          baseProps.nameWOGrade = Helpers.getSealedWaxName(entity, { hideBoxType: true });
          baseProps.grade = entity.box_type?.name || "";
          break;
        }
        default:
          break;
      }

      return [
        ...acc,
        {
          ...baseProps,
          is_trend: false,
          dataKey: `${entity.id}_${DATA_KEY_BY_CHART[selectedGraph]}`,
          key: `${entity.id}_${DATA_KEY_BY_CHART[selectedGraph]}`,
          dot: true,
        },
        {
          ...baseProps,
          is_trend: true,
          dataKey: `${entity.id}_${DATA_KEY_BY_CHART[selectedGraph]}_trend`,
          key: `${entity.id}_${DATA_KEY_BY_CHART[selectedGraph]}_trend`,
          dot: false,
          strokeDasharray: "5 5",
        },
      ];
    }, []);
    setLineProps(newLineProps);
  }, [entities, selectedGraph, hiddenLines]);

  const reset = () => {
    setCards([]);
    setSealedWaxes([]);
    setPointsByDay([]);
    setHiddenLines({});
    setTimespanPoints({ [TIMESPAN_SETTINGS.DAY]: [], [TIMESPAN_SETTINGS.WEEK]: [], [TIMESPAN_SETTINGS.MONTH]: [] });
    setHiddenLines({});
    setLineProps([]);
  };

  const calculateDataByTimespan = (timespan) => {
    if (timespan === TIMESPAN_SETTINGS.WEEK || timespan === TIMESPAN_SETTINGS.MONTH) {
      const pointsByTime = pointsByDay.reduce((acc, dayVal) => {
        const { name } = dayVal;
        const weekTimestampObj = moment(name, "MM/DD/YYYY");
        const weekTimestamp = weekTimestampObj.startOf(timespan).unix();
        if (acc[weekTimestamp]) {
          acc[weekTimestamp] = [...acc[weekTimestamp], dayVal];
        } else {
          acc[weekTimestamp] = [dayVal];
        }
        return acc;
      }, {});

      const data = Object.keys(pointsByTime).map((unixTimestamp) => {
        const days = pointsByTime[unixTimestamp];
        let countInWeek = 0;
        const mergedDays = days.reduce((weekAcc, dayVal) => {
          countInWeek += 1;
          Object.keys(dayVal).forEach((entry) => {
            const value = dayVal[entry];
            if (weekAcc[entry]) {
              const dataType = entry.slice(entry.indexOf("_") + 1);
              const entityId = entry.slice(0, entry.indexOf("_"));
              if (dataType === "avg") {
                weekAcc[`${entityId}_total_avg`] = weekAcc[`${entityId}_total_avg`]
                  ? weekAcc[`${entityId}_total_avg`] + value
                  : value;
                weekAcc[`${entityId}_total_avg_count`] = weekAcc[`${entityId}_total_avg_count`]
                  ? weekAcc[`${entityId}_total_avg_count`] + 1
                  : 1;
                weekAcc[entry] = weekAcc[`${entityId}_total_avg`] / weekAcc[`${entityId}_total_avg_count`];
              } else if (dataType === "avg_trend") {
                weekAcc[entry] = value;
              } else if (dataType === "count") {
                weekAcc[entry] += value;
              } else if (dataType === "max") {
                weekAcc[entry] = Math.max(weekAcc[entry], value);
              } else if (dataType === "min") {
                weekAcc[entry] = Math.min(weekAcc[entry], value);
              } else if (dataType === "percentage") {
                weekAcc[entry] += value;
              } else if (dataType === "sales_volume") {
                weekAcc[entry] += value;
              } else if (dataType === "total") {
                weekAcc[entry] += value;
              } else if (dataType === "totalsales") {
                weekAcc[entry] += value;
              }
            } else {
              weekAcc[entry] = value;
            }
          });
          return weekAcc;
        }, {});
        Object.keys(mergedDays)
          .filter((key) => key.includes("percentage"))
          .forEach((percentageKey) => {
            mergedDays[percentageKey] = mergedDays[percentageKey] / countInWeek;
          });
        const timestampObj = moment.unix(+unixTimestamp);
        return {
          ...mergedDays,
          name: timestampObj.format("MM/DD/YYYY"),
          sortValue: `day_${timestampObj.format("YYYYMMDD")}`,
        };
      });
      return data;
    } else {
      return pointsByDay;
    }
  };

  const toggleLineHide = (entityId) => {
    const newHiddenLines = { ...hiddenLines };
    newHiddenLines[entityId] = !(newHiddenLines[entityId] && newHiddenLines[entityId] === true);
    setHiddenLines(newHiddenLines);
  };

  /**
   * Set newly updated image for the card
   */
  const setNewImage = ({ id, image_url }) => {
    const data = cloneDeep(cards);
    const updatedCard = data.find((el) => el.id === id);
    if (updatedCard) {
      updatedCard.image_url = `${image_url}?updated=${Date.now()}`;
      setCards(data);
    }
  };

  return {
    pointsByDay,
    timespanPoints,
    setPointsByDay,
    cards,
    setCards,
    sealedWaxes,
    setSealedWaxes,
    lineProps,
    timespanSetting,
    setTimespanSetting,
    trendSetting,
    setTrendSetting,
    yAxisSetting,
    setYAxisSetting,
    calculateDataByTimespan,
    setEntityActiveAlert,
    hoveringEntityId,
    setHoveringEntityId,
    hiddenLines,
    toggleLineHide,
    setNewImage,
    selectedGraph,
    setSelectedGraph,
    reset,
    setTimespanPoints,
  };
}

const [PopularChartProvider, usePopularChartContext] = createUseContext(usePopularChart);

export { PopularChartProvider, usePopularChartContext };
